import {TimelineLite, TweenLite} from "gsap";
import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {TheRaveSymbol} from "../symbol/TheRaveSymbol";
import {MathHelper} from "../../core/utils/MathHelper";
import {TheRave} from "../TheRave";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {TheRaveGameConfig} from "../TheRaveGameConfig";
import {TheRaveSymbolState} from "../symbol/TheRaveSymbolConfig";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TheRaveSplitSymbol} from "../symbol/TheRaveSplitSymbol";
import {TheRaveXWaysSymbol} from "../symbol/TheRaveXWaysSymbol";
import {TheRaveWildSymbol} from "../symbol/TheRaveWildSymbol";
import {TheRaveScatterSymbol} from "../symbol/TheRaveScatterSymbol";
import {TheRaveEnhancerReelView} from "./TheRaveEnhancerReelView";

export class TheRaveEnhancerReelController {
    public readonly events: APIEventSystem;

    public reelId: number;
    public reelView: TheRaveEnhancerReelView;
    public isQuickStop: boolean;

   private _symsNumTotal: number;
    private _currentReelSet: string[]; //The reelSet currently in use, it might be different than the actually reelSet because its made of many different parts.
    private _currentReelSetPosition: number; // The current position in the reel
    private _currentFullReelSet: string[]; // The full actually reelSet that the most part of the _currentReelSet is make of.

    private _stopCounter: number = 0;

    private _normalStartStepDuration: number = 0.15;
    private _fastStartStepDuration: number = 0.1;
    private _startEase: string = `circ.in`;
    private _defaultSpinStepDuration: number = 0.04;
    private _normalStopStepDuration: number = 0.15;
    private _fastStopStepDuration: number = 0.1;
    private _normalStopEase: string = "rough({ template: none.inOut, strength: 5, points: 20, taper: 'in', randomize: false, clamp: false})";
    private _fastStopEase: string = this._normalStopEase;
    private _reelStopDelayTimer:TweenLite | null; //Delay for reel stop
    private _reelState: ReelState;

    get mostLeftReel(): number {
        return -1;
    }

    get reelState(): ReelState {
        return this._reelState;
    }

    set reelState(value: ReelState) {
        if (value === this._reelState) {
            return;
        }

        const oldState = this._reelState;
        this._reelState = value;
        this.events.trigger("ReelStateChange", <IReelStateEventData>{
            reelId: this.reelId,
            newState: this._reelState,
            oldState: oldState
        });
    }

    constructor(reelId: number) {
        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data: IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this.reelId = reelId;
        this.reelState = ReelState.IDLE;
        this._symsNumTotal = 1;
        this.reelView = new TheRaveEnhancerReelView(reelId);
        this.initSymbols();
    }

    private initSymbols(): void {
        this._currentFullReelSet = this.getReelSet(TheRave.model.parsedInitData.nextReelSetName);
        this._currentReelSet = this.makeUpStopReelSet(TheRave.model.reels[this.reelId].concat());
        this._currentReelSetPosition = this._currentReelSet.length;

        for (let i: number = 0; i < this._symsNumTotal; i++) {
            this.addNextNewSymbol(TheRaveSymbolState.NORMAL);
        }
        this.reelView.initSymbolsPosition(0);
    }

    private getReelSet(name: string): string[] {
        if (TheRaveGameConfig.REEL_SETS[name]) {
            const reelSet: string[] = TheRaveGameConfig.REEL_SETS[name][this.reelId];
            if (reelSet.length < 5) {
                Logger.logDev(`ReelController.getReelSet() name = ${name}, reelId = ${this.reelId}, the reelSet is too short that might break ReelController.randomizeReelSetPosition().`)
            }
            return reelSet;
        }

        throw new Error(`ReelController.getReelSet(): ReelSet ${name} is missing from GameConfig!`);
    }

    // only for spin here
    private makeUpStopReelSet(stopReelSet: string[]): string[] {
        const randomPosition: number = this.randomizeReelSetPosition(this._currentFullReelSet);
        return this._currentFullReelSet.slice(0, randomPosition + 1).concat(stopReelSet);
    }

    // Get a position where the symbol is not part of a stacked symbol unless it's the bottom of the stacked symbol.
    private randomizeReelSetPosition(reelSet: string[]): number {
        let position: number = MathHelper.randomNumberInRange(4, reelSet.length - 1); // Start on 4 to make sure there is enough number included.
        return position;
    }

    private addNextNewSymbol(state: TheRaveSymbolState, init: boolean = false): TheRaveSymbol {
        let symName: string;
            if(this._currentReelSet.length == 0) {
                if(this._currentFullReelSet == null) {
                    debugger;
                    throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
                }
                this._currentReelSet = this._currentFullReelSet.concat();
            }
            symName = this._currentReelSet.pop()!;

        let symbol: TheRaveSymbol = this.createSymbol(symName, this.reelId, state);
        this.reelView.addSymbol(symbol);
        this.reelView.allSymbols.unshift(symbol);

        const previousSymbol = this.reelView.allSymbols[1];
        // StackedSymbol.updateStackedSymbols(symbol, previousSymbol, -1, true);
        if (previousSymbol) {
            symbol.y = previousSymbol.y - previousSymbol.symbolHeight * 0.5 - symbol.bottomPadding - symbol.symbolHeight * 0.5;
        }
        return symbol;
    }

    public createSymbol(symName: string, reelId: number, state: TheRaveSymbolState, symHeight: number = 150): TheRaveSymbol {
        if (symName === "XS") {
            return new TheRaveSplitSymbol(symName, this.reelId, state, symHeight);
        } else if (symName === "XW") {
            return new TheRaveXWaysSymbol(symName, this.reelId, state, symHeight);
        } else if (symName === "W") {
            return new TheRaveWildSymbol(symName, this.reelId, state, symHeight);
        } else if (symName === "S") {
            return new TheRaveScatterSymbol(symName, this.reelId, state, symHeight);
        }
        return new TheRaveSymbol(symName, this.reelId, state, symHeight);
    }

    private removeBottomSymbol(): void {
        const bottomSymbol = this.reelView.allSymbols.pop()!;
        this.reelView.removeSymbol(bottomSymbol);
    }

    private setNextState(reelState: ReelState, duration?: number, ease?: string): void {
        // Logger.logDev(`reel ${this.reelId} set next state ${reelState}.`);
        this.reelView.nextSpinStepConfig = {
            reelState: reelState,
            duration: duration,
            ease: ease
        }
    }

    public startSpin(active: boolean): TimelineLite {
         Logger.logDev(`reel ${this.reelId} startSpin.`);

        this._stopCounter = 0;
        this.isQuickStop = false;
        this.reelView.wantBounce = false;

        if (!active) {
            this.reelState = ReelState.INACTIVE;
            return new TimelineLite();
        } else {
            this.reelView.startSpin();
            this.setNextState(
                ReelState.START,
                TheRave.apiPlugin.settings.get(APISetting.FAST_SPIN) ? this._fastStartStepDuration : this._normalStartStepDuration,
                this._startEase
            );
            const tl = new TimelineLite({
                onComplete: () => {
                    if (active) {
                        // Logger.logDev(`reel id = ${this.reelId}, set spin state on start complete.`);
                        this.setNextState(ReelState.SPIN, this._defaultSpinStepDuration, 'none');
                        this.spinStep();
                    }
                }
            });
            tl.add(this.reelView.step(this.addNextNewSymbol(TheRaveSymbolState.SPIN), () => this.removeBottomSymbol()));
            return tl;
        }
    }

    public spinStep(): TimelineLite {
        const tl = new TimelineLite({
            onComplete: () => {
                if (this.reelState === ReelState.SPIN) {
                    this.spinStep();
                    return;
                }
                if (this.reelState === ReelState.STOPPING) {
                    if (this._stopCounter > 1) {
                        this._stopCounter--;
                        this.spinStep();
                        return;
                    }

                    this._stopCounter = 0;
                    this.stopStep();
                }
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(TheRaveSymbolState.SPIN), () => this.removeBottomSymbol()));
        return tl;
    }

    public playXwayEnhancerAttentionInFS():void {
        this.reelView.playXwayAttentionInFS();
    }

    public stopStep(): TimelineLite {
        this.setNextState(
            ReelState.STOPPING,
            (TheRave.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopStepDuration : this._normalStopStepDuration,
            (TheRave.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopEase : this._normalStopEase
        );
        // TODO: Reverting this for now as it breaks normal autoplay (state stuck, unless clicking/skipping). Quickspin still can break autoplay
        const tl = new TimelineLite({
            onStart: () => {
                this.reelView.wantBounce = true;
            },
            onComplete: () => {
                this.reelState = ReelState.STOPPED;
                //console.log("REEL STATE STOPPED FOR REEL: " + this.reelId);
            }
        });
        //tl.add(() => {this.reelView.wantBounce = true});
        tl.add(this.reelView.step(this.addNextNewSymbol(TheRaveSymbolState.NORMAL), () => this.removeBottomSymbol()));
        this.reelView.allSymbols.forEach((symbol) => {
            tl.add(symbol.getStateAnimation(TheRaveSymbolState.NORMAL, {
                    duration: this.reelView.currentStepDuration,
                    alpha: 1
                }, {duration: this.reelView.currentStepDuration, alpha: 0}), 0);
        });
        // TODO: Reverting this for now as it breaks normal autoplay (state stuck, unless clicking/skipping). Quickspin still can break autoplay
        /*tl.add(() => {
            this.reelState = ReelState.STOPPED;
            console.log("REEL STATE STOPPED FOR REEL: " + this.reelId);
        });*/
        return tl;
    }

    public hasNearWin(): boolean {
        return false;
    }

    public tryStop(stopReelSet: string[], spinStopOffset: number, delay: number, quickStop: boolean): void {
        //console.log(`reel ${this.reelId} try to stop in delay ${delay}, quickStop = ${quickStop}`);
        //console.log(this.reelId + " tryStop : " + delay);
        this.isQuickStop = quickStop;

        if (this.reelState === ReelState.STOPPING || this.reelState === ReelState.STOPPED) {
            return;
        }

        if (this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
            this._reelStopDelayTimer.pause();
            this._reelStopDelayTimer.kill();
        }

        const totalDelay: number = delay;
        // Logger.logDev(`reel ${this.reelId} try to stop in extra delay ${this._extraStopDelay}, total delay ${totalDelay}`);
        if (totalDelay > 0) {
            //console.log(this.reelId + " : " + totalDelay);
            this._reelStopDelayTimer = TweenLite.to(this, totalDelay, {onComplete: () => this.tryStop(stopReelSet, 0,0, quickStop)});
            return;
        }
        this.getReadyToStop(stopReelSet);

    }

    private getReadyToStop(stopReelSet: string[]):void {
        Logger.logDev(`reel ${this.reelId} ready to stop.`);
        this._currentReelSet = this.makeUpStopReelSet(stopReelSet);
        this._stopCounter = this._symsNumTotal;
        this.reelState = ReelState.STOPPING;
        this.setNextState(ReelState.STOPPING);

    }

    public replaceSymbol(newSymName: string): void {
        const symbol: TheRaveSymbol = this.reelView.allSymbols[0];
        const currentNewSymbol: TheRaveSymbol = this.createSymbol(newSymName, this.reelId, TheRaveSymbolState.NORMAL);
        currentNewSymbol.position.set(symbol.x, symbol.y);
        this.removeOldSymbol(currentNewSymbol, symbol);
    }

    public removeOldSymbol(sym: TheRaveSymbol, oldSym: TheRaveSymbol): void {
        if (oldSym.symbolSize > 1) {
            sym.addSplitSymbolAnimation(oldSym.symbolSize, true);
        }
        this.reelView.addSymbol(sym);
        this.reelView.allSymbols[0] = sym;
        this.reelView.removeSymbol(oldSym);
        oldSym.onRemove();
    }

    public playXwayAttentionInFs(symId:number = 2): void {
            this.reelView.playXwayAttentionInFS(symId);
    }

    public xwayAttentionComplete():void {
        this.reelView.xWayAttentionComplete();
    }

    public getSymbol(index: number, isDataIndex: boolean = false, getVisibleStackedSymbol: boolean = true): TheRaveSymbol {
       return this.reelView.allSymbols[0];
    }
}